import React from "react";
import Immutable from "immutable";
import { Button, Box, ButtonToggle, ButtonToggleOption, Text, Tooltip } from "@showitinc/showit-wc";
import { initFormatter } from "../../../../utils/PropertyFormatters";
import AppConstants from "../../../../constants/AppConstants";
import UIActions from "../../../../actions/UIActions";
import AssetUtils from "../../../../utils/AssetUtils";
import PropertyPanelSectionWrapper from "../../../base/PropertyPanelSectionWrapper";
import styles from "./GalleryImagesProperties.module.css";
import SmallImageListView from "./SmallImageListView";
import LargeImageListView from "./LargeImageListView";

const {
  ASSET_LIBRARY_MODE_GALLERY_CHOOSER,
  ASSET_TYPE_IMAGE,
  UPDATE_ACTION_SET,
  GALLERY_VIEW_TYPE_LIST_SMALL,
  GALLERY_VIEW_TYPE_LIST_LARGE,
} = AppConstants;

const GALLERY_ITEMS = "content.items";
const GALLERY_ITEMS_PATH = GALLERY_ITEMS.split(".");

const GalleryImagesProperties = ({
  element,
  isLocked,
  onPropUpdate,
  title = "Gallery Images",
  onToggle,
  open,
}) => {
  const viewType = element.getIn(["gallery", "view"], GALLERY_VIEW_TYPE_LIST_SMALL);
  const { onChange } = initFormatter(onPropUpdate);

  const _onPropUpdate = (propInfo) => {
    onPropUpdate && onPropUpdate(propInfo);
  };

  const _setAssetList = (assetList) => {
    // assetList is a standard array

    if (onPropUpdate) {
      const images = Immutable.List(
        assetList.map((item) =>
          Immutable.Map(item?.asset_id ? AssetUtils.getGalleryImageAsset(item) : item),
        ),
      );

      const propInfo = {
        name: GALLERY_ITEMS,
        value: images,
        action: UPDATE_ACTION_SET,
      };

      _onPropUpdate(propInfo);
    }
  };

  const _selectAsset = () => {
    const images = element.getIn(GALLERY_ITEMS_PATH)?.toJS() || [];

    const config = {
      title: "Manage Images",
      btnLabel: "Save",
      mode: ASSET_LIBRARY_MODE_GALLERY_CHOOSER,
      onSelect: _setAssetList,
      filter: ASSET_TYPE_IMAGE,
      galleryAssets: images,
    };

    UIActions.toggleAssetChooser(config);
  };

  const _removeImage = (idx) => {
    const images = element.getIn(GALLERY_ITEMS_PATH).remove(idx);

    const propInfo = {
      name: "content.items",
      value: images,
      action: UPDATE_ACTION_SET,
    };

    _onPropUpdate(propInfo);
  };

  // Reorders images in gallery by moving an image from one index to another
  // This component is called in child component
  const _moveImage = (moveData) => {
    let images = element.getIn(GALLERY_ITEMS_PATH);

    const image = images.get(moveData.item.index);

    images = images.splice(moveData.item.index, 1);
    images = images.splice(moveData.target.index, 0, image);

    const propInfo = {
      name: GALLERY_ITEMS,
      value: images,
      action: UPDATE_ACTION_SET,
    };

    _onPropUpdate(propInfo);
  };

  const images = element.getIn(GALLERY_ITEMS_PATH);

  return (
    <PropertyPanelSectionWrapper
      title={title}
      titleIcon={images && images.size ? "galleryElement" : "add"}
      onSIPanelToggle={onToggle}
      open={open}
    >
      <Box px="3" py="2" className={styles["manage-images-sticky-btn"]}>
        <Tooltip>
          <span slot="content">
            <Text>Manage Images</Text>
          </span>
          <Button flex="1" variant="outline" onClick={_selectAsset} disabled={isLocked}>
            Manage
          </Button>
        </Tooltip>
        <ButtonToggle
          label="Layout"
          hideLabel
          value={viewType}
          onSIChange={onChange("gallery.view", viewType, true, false, GALLERY_VIEW_TYPE_LIST_SMALL)}
          className={styles["layout-toggle"]}
        >
          <Tooltip>
            <span slot="content">
              <Text>Small previews</Text>
            </span>
            <ButtonToggleOption
              aria-label="Gallery Small View"
              icon="galleryListSmall"
              value={GALLERY_VIEW_TYPE_LIST_SMALL}
              checked={viewType === GALLERY_VIEW_TYPE_LIST_SMALL}
            />
          </Tooltip>
          <Tooltip>
            <span slot="content">
              <Text>Large previews</Text>
            </span>
            <ButtonToggleOption
              aria-label="Gallery Large View"
              icon="galleryListLarge"
              value={GALLERY_VIEW_TYPE_LIST_LARGE}
              checked={viewType === GALLERY_VIEW_TYPE_LIST_LARGE}
            />
          </Tooltip>
        </ButtonToggle>
      </Box>
      {viewType === GALLERY_VIEW_TYPE_LIST_SMALL && (
        <SmallImageListView
          images={images}
          isLocked={isLocked}
          moveImage={_moveImage}
          removeImage={_removeImage}
          onPropUpdate={_onPropUpdate}
        />
      )}
      {viewType === GALLERY_VIEW_TYPE_LIST_LARGE && (
        <LargeImageListView
          images={images}
          isLocked={isLocked}
          onPropUpdate={_onPropUpdate}
          removeImage={_removeImage}
        />
      )}
    </PropertyPanelSectionWrapper>
  );
};
export default GalleryImagesProperties;
